// Shared hazards definitions; values and helpers are maintained here.
import { STATUS, CHROME } from './palette';


// --- Hazard adapters (phase 6) ----------------------------------------------
// This phase is authorised on the condition that nothing transmits, so
// "what would have gone out, and why it didn't" is the only observable
// output it has. Two vocabularies, both deliberately toned to match that:
//
// A "suppressed" decision at cold-start is the system behaving CORRECTLY
// (alerting is cold-start-silent so starting up never floods the mesh) — it
// must never read as a failure. Same reasoning that kept AUDIT_VERDICT_INFO
// from borrowing STATUS.good for "nothing detected": here it cuts the other
// way, keeping "suppressed" off STATUS.critical/serious so 382 suppressed
// rows don't read as 382 problems. "would_broadcast" gets the one
// attention-worthy tone, since that's the row someone should actually look
// at closely.
export type HazardDecision = "would_broadcast" | "suppressed";


// Explicit order, same convention as PROTOCOL_ORDER/AUDIT_VERDICT_ORDER —
// a typed array to iterate, so callers never need `Object.keys(...) as
// HazardDecision[]` (the same class of cast-past-a-Record the coordinator
// asked to close, even though this particular one was self-referential and
// safe: enumerating a Record's own keys back to its own key type, not
// casting untrusted runtime data).
export const HAZARD_DECISION_ORDER: HazardDecision[] = ["would_broadcast", "suppressed"];


export interface HazardDecisionInfo {
  label: string;
  color: { light: string; dark: string };
}


export const HAZARD_DECISION_INFO: Record<HazardDecision, HazardDecisionInfo> = {
  would_broadcast: { label: "Would broadcast", color: STATUS.warning },
  suppressed: { label: "Suppressed", color: CHROME.inkMuted },
};


export function isHazardDecision(x: string | null | undefined): x is HazardDecision {
  return x === "would_broadcast" || x === "suppressed";
}


export function hazardDecisionInfo(raw: string | null | undefined): HazardDecisionInfo {
  return isHazardDecision(raw) ? HAZARD_DECISION_INFO[raw] : { label: raw || "Unknown", color: CHROME.inkMuted };
}


// Adapter health — same shape/intent as ConnectionState above (this app's
// generic "is this upstream feed working" vocabulary), not a bespoke one.
export type HazardAdapterStatus = "ok" | "error" | "stale" | "never_run";


export interface HazardAdapterStatusInfo {
  label: string;
  color: { light: string; dark: string };
}


export const HAZARD_ADAPTER_STATUS_INFO: Record<HazardAdapterStatus, HazardAdapterStatusInfo> = {
  ok: { label: "OK", color: STATUS.good },
  error: { label: "Error", color: STATUS.critical },
  stale: { label: "Stale", color: STATUS.warning },
  never_run: { label: "Never run", color: CHROME.inkMuted },
};


export function isHazardAdapterStatus(x: string | null | undefined): x is HazardAdapterStatus {
  return x === "ok" || x === "error" || x === "stale" || x === "never_run";
}


export function hazardAdapterStatusInfo(raw: string | null | undefined): HazardAdapterStatusInfo {
  return isHazardAdapterStatus(raw) ? HAZARD_ADAPTER_STATUS_INFO[raw] : { label: raw || "Unknown", color: CHROME.inkMuted };
}


// --- Hazard severity (hazards.sources[].min_severity) -----------------------
// The one throttle each family has, per the schema's own description:
// "There is no second, upstream-vocabulary filter anywhere; raising this is
// the only throttle." Fixed order low -> high, rendered as tabs (Alerts is
// a no-dropdowns surface by design), never a <select>.
export type HazardSeverity = "routine" | "priority" | "immediate";


export const HAZARD_SEVERITY_ORDER: HazardSeverity[] = ["routine", "priority", "immediate"];


export const HAZARD_SEVERITY_LABEL: Record<HazardSeverity, string> = {
  routine: "Routine",
  priority: "Priority",
  immediate: "Immediate",
};


export function isHazardSeverity(x: string | null | undefined): x is HazardSeverity {
  return x === "routine" || x === "priority" || x === "immediate";
}


// --- Broadcast automations (Alerts > Broadcasts) ----------------------------
// Modeled on MeshMonitor's own automation menu as the design target: guided,
// no-code setup for automations, alerts, auto-replies, and timed sends.
// Rebuilt 2026-09-20 as purpose-named guided sections (MeshMonitor's
// own model: AutoAcknowledgeSection, AutoAnnounceSection,
// AutoWelcomeSection, and a similar "Cold-Start Grace" concept) against a real
// backend contract (GET/PATCH /api/automations, POST .../preview) rather
// than a generic rule builder — the generic version was rejected outright.
// Kind is the backend's own
// fixed enum; label/description here are this UI's copy, not invented
// backend behaviour.
export type AutomationKind = "auto_reply" | "scheduled_announcement" | "welcome_node" | "cold_start_grace";


export interface AutomationKindInfo {
  label: string;
  description: string;
}


export const AUTOMATION_KIND_INFO: Record<AutomationKind, AutomationKindInfo> = {
  auto_reply: {
    label: "Auto-reply to test / ping messages",
    description: "Replies automatically when a message matches a pattern — MeshMonitor's Auto-Acknowledge.",
  },
  scheduled_announcement: {
    label: "Scheduled announcement",
    description: "Sends a status message on a recurring schedule — MeshMonitor's Auto-Announce.",
  },
  welcome_node: {
    label: "Welcome new nodes",
    description: "Sends a one-time greeting the first time a new node is seen — MeshMonitor's Auto-Welcome.",
  },
  cold_start_grace: {
    label: "Cold-start grace period",
    description: "Suppresses broadcasts for a window after startup, so starting up never floods the mesh.",
  },
};


export function automationKindInfo(kind: string): AutomationKindInfo {
  return kind in AUTOMATION_KIND_INFO ? AUTOMATION_KIND_INFO[kind as AutomationKind] : { label: kind, description: "" };
}
